import { oc } from "@orpc/contract";
import { z } from "zod";
import { OptionalId } from "./product";

/**
 * What a question is about: pasvorm, bestellen, betalen, verzenden, zakelijk. The shop's FAQ page
 * groups on it, in this order.
 */
export const FaqTopic = z.enum(["fit", "ordering", "payment", "shipping", "business"]);
export type FaqTopic = z.infer<typeof FaqTopic>;

/** A frequently asked question as the admin edits it. `answer` is markdown; the shop renders it. */
export const FaqItem = z.object({
	id: z.string(),
	question: z.string(),
	answer: z.string(),
	published: z.boolean(),
	/** The group whose page shows it (a wisserpagina); `null`: only the FAQ page. */
	categoryId: z.string().nullable(),
	topic: FaqTopic,
	/** Goes up by one on every save; an update sends the version it was loaded at. */
	version: z.number().int(),
	createdAt: z.iso.datetime(),
	updatedAt: z.iso.datetime(),
});
export type FaqItem = z.infer<typeof FaqItem>;

/** What the admin's form sends to create a question. A new question goes to the end of the list. */
export const FaqItemFields = z.object({
	question: z.string().trim().min(1).max(500),
	answer: z.string().trim().min(1).max(20_000),
	published: z.boolean(),
	/** `null`, empty or left out: no group. */
	categoryId: OptionalId,
	topic: FaqTopic,
});

/** What the form sends to save a question: every field, plus the `version` it loaded. */
export const FaqItemUpdate = FaqItemFields.extend({ version: z.number().int().positive() });

/** The new order: the id of every question, each once, first to last. */
export const FaqOrder = z.object({
	ids: z
		.array(z.string())
		.max(1000)
		.refine((ids) => new Set(ids).size === ids.length, "each question once"),
});

/**
 * A published question as the shop shows it: a wisserpagina shows those of its group (by slug), in
 * order; the FAQ page groups them by topic.
 */
export const PublishedFaqItem = z.object({
	id: z.string(),
	question: z.string(),
	answer: z.string(),
	/** The slug of the group whose page shows it; `null`: none. */
	categorySlug: z.string().nullable(),
	topic: FaqTopic,
});
export type PublishedFaqItem = z.infer<typeof PublishedFaqItem>;

const ById = z.object({ id: z.string() });

/**
 * Frequently asked questions, in the order Richard sets. `/admin/...` needs a staff session and
 * lists every question in order; `/shop/faq-items` is public and lists the published ones in order.
 */
export const FaqItemContract = {
	list: oc.route({ method: "GET", path: "/admin/faq-items" }).output(z.array(FaqItem)),
	/** Errors: `FaqItemCategoryNotFound` (422). */
	create: oc
		.route({ method: "POST", path: "/admin/faq-items", successStatus: 201 })
		.input(FaqItemFields)
		.output(FaqItem),
	get: oc.route({ method: "GET", path: "/admin/faq-items/{id}" }).input(ById).output(FaqItem),
	/** Errors: `FaqItemNotFound` (404), `FaqItemChanged` (409), `FaqItemCategoryNotFound` (422). */
	update: oc
		.route({ method: "PUT", path: "/admin/faq-items/{id}" })
		.input(FaqItemUpdate.extend(ById.shape))
		.output(FaqItem),
	delete: oc.route({ method: "DELETE", path: "/admin/faq-items/{id}", successStatus: 204 }).input(ById),
	/** Sets the order of every question at once; answers the list in its new order. */
	reorder: oc
		.route({ method: "PUT", path: "/admin/faq-items/order" })
		.input(FaqOrder)
		.output(z.array(FaqItem)),
	listPublished: oc.route({ method: "GET", path: "/shop/faq-items" }).output(z.array(PublishedFaqItem)),
};
